var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
axios.interceptors.response.use((response) => response, (error) => {
    return Promise.reject(error);
});
// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
function parseJwt(token) {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(atob(base64)
        .split("")
        .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(""));
    return JSON.parse(jsonPayload);
}
class VeritasAPI {
    constructor(baseUrl, email, password) {
        this.AUTH_URL = "https://roadbotics-cloudgate-prod.appspot.com/user/auth";
        this.EXPIRATION_BUFFER = 60 * 5; // arbitrary 5 minute buffer from expiration to prevent races
        this.getAuthToken = (email, password) => __awaiter(this, void 0, void 0, function* () {
            let timestamp = new Date().getTime() / 1000;
            if (!this.authToken ||
                this.expiration - this.EXPIRATION_BUFFER <= timestamp) {
                const authToken = yield this.authorize(email, password);
                this.authToken = authToken;
                return authToken;
            }
            else {
                return this.authToken;
            }
        });
        this.authorize = (email, password) => __awaiter(this, void 0, void 0, function* () {
            const results = yield axios.get(this.AUTH_URL, {
                auth: {
                    username: email,
                    password: password,
                },
            });
            // save expiration timestamp in seconds from jwt data
            const jwtData = parseJwt(results.data);
            this.expiration = parseInt(jwtData.exp);
            return results.data;
        });
        this.getRoadSensePointsGeoJson = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/geojson/points`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            const downloadURL = response.data["presigned_url"];
            return this.downloadBlobFromURL(downloadURL);
        });
        this.regenerateRoadSenseTiles = (params) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            let data = {
                max_zoom: params.maxZoom,
            };
            const response = yield axios.post(`${this.baseUrl}/mb/tile/${params.scanId}/roadsense`, data, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (response.status === 200) {
                return response.data;
            }
            else {
                console.error(response.statusText);
                return false;
            }
        });
        this.regenerateConditionalRatingTiles = (params) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            let data = {
                max_zoom: params.maxZoom,
            };
            const response = yield axios.post(`${this.baseUrl}/tile/${params.scanId}/conditional`, data, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (response.status === 200) {
                return response.data;
            }
            else {
                console.error(response.statusText);
                return false;
            }
        });
        this.getRatedSegmentsGeoJson = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/geojson/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["segments_empty"]) {
                const downloadURL = response.data["segments_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.getRatedPointsGeoJson = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/geojson/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["points_empty"]) {
                const downloadURL = response.data["points_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.getRatedSegmentsCSV = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/csv/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["segments_empty"]) {
                const downloadURL = response.data["segments_csv_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.getRatedPointsCSV = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/csv/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["points_empty"]) {
                const downloadURL = response.data["points_csv_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.getRatedPointsShapefile = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/shapefile/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["points_empty"]) {
                const downloadURL = response.data["points_shapefile_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.getRatedSegmentsShapefile = (scanId) => __awaiter(this, void 0, void 0, function* () {
            const scanPointURL = `${this.baseUrl}/v1/export/${scanId}/shapefile/rated`;
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(scanPointURL, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            if (!response.data["segments_empty"]) {
                const downloadURL = response.data["segments_shapefile_url"];
                return this.downloadBlobFromURL(downloadURL);
            }
            else {
                return false;
            }
        });
        this.downloadBlobFromURL = (downloadURL) => __awaiter(this, void 0, void 0, function* () {
            // remove auth header for s3 request. Otherwise you will get this error:
            //<Message> Only one auth mechanism allowed; only the X-Amz-Algorithm query parameter, Signature query string parameter or the Authorization header should be specified</Message>
            const geoJsonResponse = yield axios.get(downloadURL, {
                responseType: "blob",
                transformRequest: (data, headers) => {
                    delete headers.common["Authorization"];
                },
            });
            return geoJsonResponse.data;
        });
        this.getAvailableSegmentsForUser = (scanHash, email, idi = false) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(`${this.baseUrl}/qa/${scanHash}/available-segments`, {
                headers: {
                    Authorization: authToken,
                },
                params: {
                    email: email,
                    idi: idi,
                },
            });
            return response.data;
        });
        this.sendToRoadway = (scanHash) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            try {
                yield axios.post(`${this.baseUrl}/tile/${scanHash}/points/rated`, {}, {
                    headers: {
                        Authorization: authToken,
                        "content-type": "application/json",
                    },
                });
                yield axios.post(`${this.baseUrl}/tile/${scanHash}/segments/rated`, {}, {
                    headers: {
                        Authorization: authToken,
                        "content-type": "application/json",
                    },
                });
            }
            catch (error) {
                console.log("Error", error);
            }
        });
        this.markAsCompleted = (scanHash, segmentIds, email, ratingType = "conditional-rating") => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.post(`${this.baseUrl}/qa/${scanHash}/${ratingType}`, { segment_ids: segmentIds, email: email }, {
                headers: {
                    Authorization: authToken,
                    "content-type": "application/json",
                },
            });
            return response.data;
        });
        this.getSegmentStatus = (scanHash) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(`${this.baseUrl}/qa/${scanHash}/segments/status`, {
                headers: {
                    Authorization: authToken,
                },
            });
            return response.data;
        });
        this.setMultipleQCProjectsReviewed = (qcProjectIDs) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.put(`${this.baseUrl}/qc/project/multiple/is_reviewed`, { qc_project_ids: qcProjectIDs }, { headers: { Authorization: authToken } });
            return response.data;
        });
        this.updateIdiRatings = (scanHash, userEmail, frameId, idiStates) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.put(`${this.baseUrl}/qa/${scanHash}/distress`, {
                distresses: idiStates,
                updated_by: userEmail,
                frame_ids: frameId,
            }, { headers: { Authorization: authToken } });
            return response.data;
        });
        this.getIdiQuorumMetricSegments = (projectId) => __awaiter(this, void 0, void 0, function* () {
            const authToken = yield this.getAuthToken(this.email, this.password);
            const response = yield axios.get(`${this.baseUrl}/qc/scan/${projectId}/metrics/idi/segment`, {
                headers: {
                    Authorization: authToken,
                },
            });
            return response.data;
        });
        this.email = email;
        this.password = password;
        this.baseUrl = baseUrl;
        this.authToken = null;
        this.expiration = 0;
    }
}
export default VeritasAPI;
